import { createFileRoute } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { 
  getCapturedEmails, 
  getEmailDeliveryMode 
} from "@/lib/email-demo/email-demo.functions";
import { triggerTestNotification } from "@/lib/email-demo/trigger-test.functions";
import { triggerOrderNotificationDispatch } from "@/lib/admin/order-notifications.functions";
import { sendCustomerIoTest, sendCustomerIoWelcomeTest } from "@/lib/customerio/customerio-test.functions";
import { 
  deleteOrderNotification, 
  bulkDeleteOrderNotifications,
  sendCustomAdminEmail 
} from "@/lib/admin/email-management.functions";

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Mail, Eye, Clock, User, Hash, AlertCircle, RefreshCcw, Trash2, Edit2, Send, Search, Filter
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_admin/admin/email-logs")({
  component: EmailLogsPage,
});

function EmailLogsPage() {
  const fetchEmails = useServerFn(getCapturedEmails);
  const fetchMode = useServerFn(getEmailDeliveryMode);
  const triggerTest = useServerFn(triggerTestNotification);
  const deleteEmailFn = useServerFn(deleteOrderNotification);
  const bulkDeleteEmailsFn = useServerFn(bulkDeleteOrderNotifications);
  const sendEmailFn = useServerFn(sendCustomAdminEmail);
  const dispatchPendingFn = useServerFn(triggerOrderNotificationDispatch);
  const cioTestFn = useServerFn(sendCustomerIoTest);
  const cioWelcomeFn = useServerFn(sendCustomerIoWelcomeTest);
  const [cioTo, setCioTo] = useState("");
  const [cioSending, setCioSending] = useState(false);
  const [cioWelcomeSending, setCioWelcomeSending] = useState(false);
  const handleCioTest = async () => {
    if (cioSending) return;
    setCioSending(true);
    try {
      const r = await cioTestFn({ data: { to: cioTo } });
      if (r.ok) toast.success("Customer.io test email sent.");
      else toast.error(r.message);
    } catch {
      toast.error("Could not send the Customer.io test. Please check the address.");
    } finally {
      setCioSending(false);
    }
  };
  const handleCioWelcomeTest = async () => {
    if (cioWelcomeSending) return;
    setCioWelcomeSending(true);
    try {
      const r = await cioWelcomeFn({ data: { to: cioTo } });
      if (r.ok) toast.success("Customer.io welcome test sent.");
      else toast.error(r.message);
    } catch {
      toast.error("Could not send the Customer.io welcome test. Please check the address.");
    } finally {
      setCioWelcomeSending(false);
    }
  };


  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isTriggering, setIsTriggering] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");

  const { data: modeData } = useQuery({ queryKey: ["admin", "email-mode"], queryFn: () => fetchMode() });
  const { 
    data: emails, 
    isLoading, 
    refetch, 
    isRefetching 
  } = useQuery({
    queryKey: ["admin", "captured-emails"],
    queryFn: () => fetchEmails(),
    refetchInterval: 30000,
  });

  const filteredEmails = useMemo(() => {
    if (!emails) return [];
    return emails.filter((email: any) => {
      const demoData = email.payload?._demo_captured;
      const subject = demoData?.subject || email.kind || "";
      const matchesSearch = 
        email.recipient.toLowerCase().includes(search.toLowerCase()) ||
        subject.toLowerCase().includes(search.toLowerCase()) ||
        (email.payload?.order_ref || "").toLowerCase().includes(search.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || email.status === statusFilter || email.provider_status === statusFilter;
      const matchesKind = kindFilter === "all" || email.kind === kindFilter;
      
      return matchesSearch && matchesStatus && matchesKind;
    });
  }, [emails, search, statusFilter, kindFilter]);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEmails.length && filteredEmails.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredEmails.map((e: any) => e.id)));
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure? Deleting this log only removes the audit record. This cannot recall an email already sent.")) return;
    try {
      await deleteEmailFn({ data: { id } });
      toast.success("Log deleted");
      refetch();
    } catch (e) {
      toast.error("Failed to delete log");
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedIds.size} selected logs? This only removes audit records and cannot recall emails.`)) return;
    setIsDeleting(true);
    try {
      await bulkDeleteEmailsFn({ data: { ids: Array.from(selectedIds) } });
      toast.success(`${selectedIds.size} logs deleted`);
      setSelectedIds(new Set());
      refetch();
    } catch (e) {
      toast.error("Bulk delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTriggerTest = async () => {
    setIsTriggering(true);
    try {
      await triggerTest({ data: { kind: 'customer_order_received' } });
      toast.success("Test email generated");
      refetch();
    } catch (err) {
      toast.error("Failed to generate test notification");
    } finally {
      setIsTriggering(false);
    }
  };

  const handleDispatchPending = async () => {
    setIsDispatching(true);
    try {
      const summary: any = await dispatchPendingFn({ data: undefined } as any);
      toast.success(
        `Dispatched: ${summary?.sent ?? 0} sent, ${summary?.failed ?? 0} failed, ${summary?.skipped ?? 0} skipped`,
      );
      refetch();
    } catch (err) {
      toast.error("Dispatch failed");
    } finally {
      setIsDispatching(false);
    }
  };

  const mode = modeData?.mode || 'demo';

  return (
    <div className="space-y-6 pb-20">
      <AdminPageHeader 
        title="Email Notification Logs" 
        description="Audit communications, preview templates, and send follow-ups." 
      />

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Badge variant={mode === 'demo' ? 'secondary' : 'default'} className="h-6">
            {mode === 'demo' ? 'DEMO EMAIL MODE' : 'PRODUCTION MODE'}
          </Badge>
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground animate-pulse">
            <Clock className="h-3 w-3" /> Auto-refresh: 30s
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="default" size="sm" onClick={handleTriggerTest} disabled={isTriggering}>
            <RefreshCcw className={cn("h-4 w-4 mr-2", isTriggering && "animate-spin")} />
            {isTriggering ? "Generating..." : "Generate Test Order Email"}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleDispatchPending} disabled={isDispatching}>
            <RefreshCcw className={cn("h-4 w-4 mr-2", isDispatching && "animate-spin")} />
            {isDispatching ? "Sending..." : "Send Pending Now"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading || isRefetching}>
            <RefreshCcw className={cn("h-4 w-4 mr-2", isRefetching && "animate-spin")} />
            Refresh
          </Button>
          <Input
            className="h-8 w-56"
            placeholder="Customer.io test address"
            aria-label="Customer.io test address"
            value={cioTo}
            onChange={(e) => setCioTo(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={handleCioTest} disabled={cioSending || !cioTo}>
            {cioSending ? "Sending..." : "Send Customer.io test"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCioWelcomeTest} disabled={cioWelcomeSending || !cioTo}>
            {cioWelcomeSending ? "Sending..." : "Send welcome test"}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search email, subject, or order #"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="captured">Captured (Demo)</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="customer_order_received">Order Received</SelectItem>
                <SelectItem value="admin_new_order">Admin Alert</SelectItem>
                <SelectItem value="customer_status_updated">Status Update</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Checkbox 
              id="select-all" 
              checked={selectedIds.size === filteredEmails.length && filteredEmails.length > 0} 
              onCheckedChange={toggleSelectAll} 
            />
            <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">Select All</label>
          </div>
          {selectedIds.size > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={isDeleting} className="h-8">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Selected ({selectedIds.size})
            </Button>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          Showing {filteredEmails.length} of {emails?.length || 0} logs
        </div>
      </div>

      <div className="grid gap-4">
        {isLoading ? (
          [1, 2, 3].map(i => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)
        ) : filteredEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/20 rounded-lg border border-dashed">
            <Mail className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium">No logs found</h3>
          </div>
        ) : (
          filteredEmails.map((email: any) => (
            <EmailLogCard 
              key={email.id} 
              email={email} 
              isSelected={selectedIds.has(email.id)}
              onToggle={() => toggleSelect(email.id)}
              onDelete={() => handleDelete(email.id)}
              onRefetch={refetch}
              sendEmailFn={sendEmailFn}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EmailLogCard({ email, isSelected, onToggle, onDelete, onRefetch, sendEmailFn }: any) {
  const demoData = email.payload?._demo_captured;
  const isDemo = email.provider_status === 'captured' || !!demoData;
  const subject = demoData?.subject || `Notification: ${email.kind}`;

  return (
    <Card className={cn("overflow-hidden border-l-4 transition-all", isSelected ? "border-l-primary bg-primary/5" : "border-l-primary/20 hover:border-l-primary")}>
      <CardContent className="p-0">
        <div className="flex items-center gap-4 p-4">
          <Checkbox checked={isSelected} onCheckedChange={onToggle} />
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{subject}</span>
              <Badge variant="outline" className="text-[10px] h-4 uppercase">{email.kind.replace(/_/g, ' ')}</Badge>
              {isDemo && (
                <Badge variant="secondary" className="text-[10px] h-4 bg-orange-100 text-orange-700">DEMO</Badge>
              )}
              <Badge variant={email.status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px] h-4">
                {email.status}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-1"><User className="h-3 w-3" />{email.recipient}</div>
              <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{format(new Date(email.created_at), "MMM d, HH:mm:ss")}</div>
              <div className="flex items-center gap-1 text-primary"><Hash className="h-3 w-3" />Order: {email.payload?.order_ref || 'N/A'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <PreviewDialog email={email} />
            <EditSendDialog email={email} onRefetch={onRefetch} sendEmailFn={sendEmailFn} />
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive hover:bg-destructive/10">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewDialog({ email }: any) {
  const demoData = email.payload?._demo_captured;
  const isDemo = email.provider_status === 'captured' || !!demoData;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" className="gap-2"><Eye className="h-4 w-4" /> Preview</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> {demoData?.subject || `Email Preview: ${email.kind}`}
          </DialogTitle>
          <div className="flex gap-2 mt-2">
            <Badge variant="outline">To: {email.recipient}</Badge>
            <Badge variant="outline">Status: {email.status}</Badge>
            <Badge variant="outline">Provider: {isDemo ? 'DEMO CAPTURE' : 'RESEND'}</Badge>
          </div>
        </DialogHeader>
        <Tabs defaultValue="rendered" className="flex-1 flex flex-col min-h-0">
          <div className="px-6 border-b bg-muted/30">
            <TabsList className="bg-transparent h-10 gap-4">
              <TabsTrigger value="rendered">Rendered HTML</TabsTrigger>
              <TabsTrigger value="plain">Plain Text</TabsTrigger>
              <TabsTrigger value="raw">Raw Data</TabsTrigger>
            </TabsList>
          </div>
          <ScrollArea className="flex-1 p-6 bg-slate-50">
            <TabsContent value="rendered" className="mt-0">
              {demoData?.html ? (
                <div className="bg-white shadow-sm border rounded-lg overflow-hidden mx-auto max-w-[600px]" dangerouslySetInnerHTML={{ __html: demoData.html }} />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mb-2 opacity-20" />
                  <p>HTML content unavailable for this record.</p>
                </div>
              )}
            </TabsContent>
            <TabsContent value="plain" className="mt-0">
              <pre className="text-xs bg-white p-4 rounded border whitespace-pre-wrap font-mono">{demoData?.text || 'Plain text unavailable.'}</pre>
            </TabsContent>
            <TabsContent value="raw" className="mt-0">
              <pre className="text-xs bg-white p-4 rounded border whitespace-pre-wrap font-mono">{JSON.stringify(email, null, 2)}</pre>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function EditSendDialog({ email, onRefetch, sendEmailFn }: any) {
  const demoData = email.payload?._demo_captured;
  const [recipient, setRecipient] = useState(email.recipient);
  const [from, setFrom] = useState("orders@healthy-kitchen-test.in");
  const [subject, setSubject] = useState(demoData?.subject || `Follow-up on order ${email.payload?.order_ref || ''}`);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [open, setOpen] = useState(false);

  const presets = [
    { label: "ORDER DELAY", text: "Your order is taking longer than expected. We apologise for the delay. We will update you within 30 minutes." },
    { label: "POSSIBLE CANCELLATION", text: "We are sorry, but your order may not be fulfilled within the expected time frame. If this cannot be resolved shortly, we may need to cancel the order." }
  ];

  const handleSend = async () => {
    if (!message) return toast.error("Message is required");
    if (!recipient) return toast.error("Recipient is required");
    if (!from) return toast.error("From address is required");
    
    setIsSending(true);
    try {
      await sendEmailFn({ data: { 
        recipient,
        from,
        subject, 
        message, 
        orderId: email.order_id,
        originalNotificationId: email.id 
      }});
      toast.success("Follow-up email sent");
      setOpen(false);
      onRefetch();
    } catch (e) {
      toast.error("Failed to send email");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2"><Edit2 className="h-4 w-4" /> Edit & Send</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compose Follow-up Email</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">From</label>
              <Input value={from} onChange={e => setFrom(e.target.value)} placeholder="orders@healthy-kitchen-test.in" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">To</label>
              <Input value={recipient} onChange={e => setRecipient(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Subject</label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium flex justify-between">
              Message
              <div className="flex gap-2">
                {presets.map(p => (
                  <button key={p.label} onClick={() => setMessage(p.text)} className="text-[10px] text-primary hover:underline">{p.label}</button>
                ))}
              </div>
            </label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={6} placeholder="Enter your customer message here..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSend} disabled={isSending} className="gap-2">
            <Send className={cn("h-4 w-4", isSending && "animate-pulse")} /> 
            {isSending ? "Sending..." : "Send Follow-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
